import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getComponentFromName } from '../../../lib/utils';

import { useThemeClassName, useTheme } from '../../../contexts/ThemeContext';
import * as IconComponents from '../../icons/index';
import DrawDates from '../../partials/DrawDates';

import styles from '../../../../styles/items/draw-card.module.scss';

interface DrawCardProps {
    className?: string;
    draw: Draw | null;
}

const DrawCard: React.FC<DrawCardProps> = ({ className = null, draw = null }) => {
    const intl = useIntl();
    const { name: theme = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        type,
        image,
        title,
        description,
        legal,
        badge,
        totalPoints = null,
        participationsCount = 0,
        pointsByParticipation = null,
    } = draw || {};

    const { url: imageUrl = null } = image || {};

    const BadgeComponent = badge !== null ? getComponentFromName(IconComponents, badge) : null;

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.inner}>
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        {pointsByParticipation && pointsByParticipation > 1 ? (
                            <FormattedMessage
                                defaultMessage="{title} participations"
                                description="Draw card title"
                                values={{ title }}
                            />
                        ) : (
                            title
                        )}
                    </h2>
                    <DrawDates draw={draw} className={styles.dates} batchClassName={styles.batch} />
                </div>

                <div className={styles.card}>
                    <div className={styles.imageContainer}>
                        {imageUrl !== null ? (
                            <img className={styles.image} src={imageUrl} alt={title} />
                        ) : null}
                        {BadgeComponent !== null ? (
                            <BadgeComponent className={styles.badge} />
                        ) : null}
                    </div>
                    <div className={styles.content}>
                        {description !== null ? (
                            <h2 className={styles.description}>{description}</h2>
                        ) : null}
                        {legal !== null ? <div className={styles.legal}>{legal}</div> : null}
                    </div>
                </div>
                {totalPoints !== null ? (
                    <div className={classNames([styles.amountRow, styles.pointsRow])}>
                        <div className={styles.label}>
                            <div>
                                {type !== 'venues' ? (
                                    <FormattedMessage
                                        defaultMessage="Total points earned <strong>{time}</strong>"
                                        description="Draw card label"
                                        values={{
                                            time:
                                                type === 'weekly'
                                                    ? intl.formatMessage({
                                                          defaultMessage: 'this week',
                                                          description: 'Points time range',
                                                      })
                                                    : intl.formatMessage({
                                                          defaultMessage: 'this season',
                                                          description: 'Points time range',
                                                      }),
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Total points earned"
                                        description="Draw card label"
                                    />
                                )}
                            </div>
                            {pointsByParticipation !== null && pointsByParticipation > 1 ? (
                                <div className={styles.chances}>
                                    <FormattedMessage
                                        defaultMessage="{points} points = 1 chance"
                                        description="Draw card label"
                                        values={{
                                            points: pointsByParticipation,
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div className={styles.amount}>
                            <span className={styles.amountInner}>
                                <FormattedMessage
                                    defaultMessage="{points} pts"
                                    description="Draw card label"
                                    values={{
                                        points: totalPoints,
                                    }}
                                />
                            </span>
                        </div>
                    </div>
                ) : null}

                <div className={styles.amountRow}>
                    <div className={styles.label}>
                        {theme === 'nfl' ? (
                            <FormattedMessage
                                defaultMessage="Number of chances to win the {title}"
                                description="Draw card label"
                                values={{
                                    title: (title || '').toLowerCase(),
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Number of chances to win a {title}"
                                description="Draw card label"
                                values={{
                                    title: (title || '').toLowerCase(),
                                }}
                            />
                        )}
                    </div>
                    <div className={styles.amount}>
                        <span className={styles.amountInner}>{participationsCount}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DrawCard;
